import React, { useEffect, useState, useRef, Suspense, lazy } from 'react';
interface LazySectionProps {
  children: ReactNode;
  rootMargin?: string;
  minHeight?: number;
  fallback?: ReactNode;
}
/**
 * Mounts its children only when the placeholder enters (or nears) the viewport.
 * Combined with lazy() on the child, the section's JS chunk is fetched
 * just-in-time as the user scrolls — giving a true progressive lazy-load.
 */
export function LazySection({
  children,
  rootMargin = '200px 0px',
  minHeight = 240,
  fallback
}: LazySectionProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (visible) return;
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
          break;
        }
      }
    }, {
      rootMargin,
      threshold: 0
    });
    observer.observe(node);
    // Safety net: if for some reason the observer never fires (rare mobile
    // browser bugs, layout collapse, etc.) check once on the next frame
    // whether the element is already in view.
    const rafId = requestAnimationFrame(() => {
      const rect = node.getBoundingClientRect();
      if (rect.top < window.innerHeight + 200 && rect.bottom > -200) {
        setVisible(true);
        observer.disconnect();
      }
    });
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [visible, rootMargin]);
  const defaultFallback = <div className="w-full flex items-center justify-center" style={{
    minHeight: `${minHeight}px`
  }} data-id="element-147">
      <div className="w-8 h-8 rounded-full border-2 border-primary/20 border-t-primary animate-spin" data-id="element-148" />
    </div>;
  return <div ref={ref} style={!visible ? {
    minHeight: `${minHeight}px`
  } : undefined} data-id="element-149">
      {visible ? <Suspense fallback={fallback ?? defaultFallback} data-id="element-150">{children}</Suspense> : null}
    </div>;
}